/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Super Admin Authentication Screen (AdminLoginView.tsx)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, type FormEvent } from 'react';
import { Shield, Lock, Mail, ArrowRight, AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import { useAdminAuthStore, type AdminUser } from '../stores/useAdminAuthStore';
import { getAdminDomains } from '../lib/urls';
import { useI18n } from '../i18n';
import { useTheme } from '../context/ThemeProvider';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';
import { ThemeToggle } from '../components/common/ThemeToggle';

export const AdminLoginView: React.FC = () => {
  const { t } = useI18n();
  const { branding, isDark } = useTheme();
  const setSession = useAdminAuthStore((state) => state.setSession);

  const logoUrl = isDark ? (branding?.logoDarkUrl || branding?.logoLightUrl) : (branding?.logoLightUrl || branding?.logoDarkUrl);

  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.localhost') ||
      Boolean(import.meta.env.DEV));

  const [email, setEmail] = useState<string>(
    isLocalDev ? 'admin@sophiaplatform.com' : '',
  );
  const [password, setPassword] = useState<string>(
    isLocalDev ? 'ChangeMeImmediately123!' : '',
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleLogin = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);
    setLoading(true);

    try {
      const domains = getAdminDomains();
      const apiUrl = `${domains.apiOrigin}/api/v1/auth/login`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        let errorText = data?.error?.message;
        if (!errorText && Array.isArray(data?.error?.issues) && data.error.issues.length > 0) {
          errorText = data.error.issues
            .map((issue: any) => issue?.message)
            .filter(Boolean)
            .join('. ');
        }
        if (!errorText && typeof data?.message === 'string') {
          errorText = data.message;
        }
        if (!errorText) {
          errorText =
            response.status === 401
              ? 'Invalid email or password credentials.'
              : `Authentication failed (HTTP ${response.status})`;
        }
        throw new Error(errorText);
      }

      if (!data?.success || !data?.data?.accessToken) {
        throw new Error('Authentication response format invalid.');
      }

      const sessionData = data.data;
      const user = sessionData.user;

      if (user.role?.toLowerCase() !== 'super_admin') {
        throw new Error('Forbidden: Super Administrator privileges required.');
      }

      const adminUser: AdminUser = {
        id: user.id,
        email: user.email,
        fullName: user.fullName || 'Super Administrator',
        role: 'super_admin',
        avatarUrl: user.avatarUrl,
      };

      setSession(sessionData.accessToken, adminUser);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Authentication failed.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fast-track sign in for local development environments.
   */
  const handleQuickLocalDevSignIn = async () => {
    setEmail('admin@sophiaplatform.com');
    setPassword('ChangeMeImmediately123!');
    setErrorMessage(null);
    setLoading(true);

    try {
      const domains = getAdminDomains();
      const apiUrl = `${domains.apiOrigin}/api/v1/auth/login`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: 'admin@sophiaplatform.com',
          password: 'ChangeMeImmediately123!',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.success && data?.data?.accessToken) {
          const user = data.data.user;
          setSession(data.data.accessToken, {
            id: user.id || 'usr_super_admin',
            email: user.email || 'admin@sophiaplatform.com',
            fullName: user.fullName || 'Super Administrator',
            role: 'super_admin',
            avatarUrl: user.avatarUrl,
          });
          return;
        }
      }

      // If local api is offline or D1 not yet initialized, fallback to dev token
      const fallbackAdminUser: AdminUser = {
        id: 'usr_super_admin',
        email: 'admin@sophiaplatform.com',
        fullName: 'Super Administrator (Local Dev)',
        role: 'super_admin',
      };

      // Construct a valid client-side local dev JWT if local worker is in startup
      setSession('local_dev_mock_admin_token', fallbackAdminUser);
    } catch {
      const fallbackAdminUser: AdminUser = {
        id: 'usr_super_admin',
        email: 'admin@sophiaplatform.com',
        fullName: 'Super Administrator (Local Dev)',
        role: 'super_admin',
      };
      setSession('local_dev_mock_admin_token', fallbackAdminUser);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background flex flex-col justify-between p-4 sm:p-6 select-none relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      {/* Top Bar with Language and Theme Controls */}
      <header className="w-full flex items-center justify-between max-w-5xl mx-auto z-10">
        <div className="flex items-center gap-2.5">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={branding?.siteName || 'Sophia Platform'}
              className="h-10 w-auto max-w-[140px] object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
                const fb = e.currentTarget.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-sm"
            style={logoUrl ? { display: 'none' } : undefined}
          >
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <span className="font-heading font-bold text-sm text-foreground tracking-tight block">
              {branding?.siteName || 'Sophia Platform'}
            </span>
            <span className="text-[10px] text-muted-fg font-mono uppercase tracking-wider block">
              Edge-Native Backoffice
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>

      {/* Main Login Card */}
      <main className="w-full max-w-md mx-auto my-auto py-8 z-10">
        <div className="border border-surface-border bg-surface/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-xl space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              <Shield className="w-3.5 h-3.5" />
              <span>{t('auth.superAdmin') || 'Super Admin'}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
              {t('auth.loginRequired') || 'Control Plane Sign In'}
            </h2>
            <p className="text-xs text-muted-fg max-w-sm mx-auto">
              Authenticate with administrative credentials to access system configurations, models, and analytics.
            </p>
          </div>

          {errorMessage && (
            <div className="p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/10 flex items-start gap-3 text-xs text-rose-500 animate-in fade-in duration-200">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-semibold block">Authentication Error</span>
                <span>{errorMessage}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground block">
                Administrative Email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-muted-fg absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@sophiaplatform.com"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-border bg-surface text-foreground text-xs focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-fg/60"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground block">
                Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-muted-fg absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-border bg-surface text-foreground text-xs focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-fg/60 font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-xl bg-primary hover:bg-primary-hover text-primary-fg font-medium text-xs flex items-center justify-center gap-2 shadow-md shadow-primary/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Verifying Credentials...</span>
                </>
              ) : (
                <>
                  <span>Sign In to Control Plane</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {isLocalDev && (
            <div className="pt-4 border-t border-surface-border space-y-2.5">
              <div className="flex items-center justify-between text-[11px] text-muted-fg">
                <span className="font-mono text-emerald-500 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Local Development Detected
                </span>
                <span className="text-[10px]">Port 3002</span>
              </div>
              <button
                type="button"
                onClick={handleQuickLocalDevSignIn}
                disabled={loading}
                className="w-full py-2 px-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <span>⚡ 1-Click Sign In as Local Super Admin</span>
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-5xl mx-auto text-center py-2 z-10">
        <p className="text-[11px] text-muted-fg">
          &copy; {new Date().getFullYear()} Sophia Platform &bull; Cloudflare Edge-Native Edition
        </p>
      </footer>
    </div>
  );
};

